import { gql } from "@apollo/client";

export const userAllFieldsFragment = gql`
    fragment UserAllFields on User {
        id
        firstName
        lastName
        email
        moxtraId
        phoneNumber
        invitationPending
        loginStatus
        role
        nativeMessagingEnabled
        twilioUserId
        agreementAcceptedDate,
        rampartUniqueId,
        rampartGroups,
        clientTypes,
        stytchUserId
    }
`;

export const organizationAllFieldsFragment = gql`
    fragment OrganizationAllFields on Organization {
        id
        stripeCid
        name
        isAgency
        createdAt
        updatedAt
    }
`;

export const storeAllFieldsFragment = gql`
    ${userAllFieldsFragment}
    ${organizationAllFieldsFragment}
    fragment StoreAllFields on Store {
        id
        name
        about
        avatar
        demographic
        brandColors
        similarBrands
        logoUrl
        youtube
        twitter
        instagram
        tiktok
        twitch
        createdAt
        updatedAt
        internalVendorName
        isPartnershipsTier
        domain
        conversationSid
        julySleepExcluded
        organizations {
            ...OrganizationAllFields
            members {
                ...UserAllFields
            }
        }
    }
`;

export const storeForAdminFieldsFragment = gql`
    fragment StoreForAdminFields on Store {
        id
        name
        internalVendorName
        isPartnershipsTier
        julySleepExcluded
        domain
        createdAt
        updatedAt
    }
`;

export const storeForReportingFieldsFragment = gql`
    fragment StoreForReportingFields on Store {
        id
        name
        internalVendorName
        isPartnershipsTier
    }
`;

// TODO: refactor this and `productListingAllFieldsFragment` to use common fragments.
export const productListingForAdminFieldsFragment = gql`
    fragment ProductListingForAdminFields on ProductListing {
        id
        createdAt
        updatedAt
        submissionDate
        qualifiedDate
        customerStageSheetId
        name
        description
        mediaAssets
        launchType
        fulfillmentType
        inventoryOwner
        sizing
        status
        modifier
        assignee
        designer
        dqReason
        dqDate
        dqStage
        sku
        source
        lead
        agency
        notes
        launchDate
        launchEnd
        warehousePricingVersion
        lifecycleStatus
        preDesignReviewState
        designConceptState
        mfgDrawingState
        samplingState
        designInvoiceState
        packagingState
        onlineListingState
        inventoryStatus
        storeWorkstreamState
        mfgWorkstreamState
        addOns {
            id
            name
        }
        pricing {
            catalogOption {
                id
                name
                displayName
            }
        }
        store {
            id
            name
            domain
        }
        twilioConversationId
        asanaTaskId
        shopifyProductId
        shippingInfo
    }
`;

export const productListingAllFieldsFragment = gql`
    fragment ProductListingAllFields on ProductListing {
        id
        name
        description
        createdAt
        updatedAt
        submissionDate
        mediaAssets
        launchType
        fulfillmentType
        inventoryOwner
        sizing
        status
        store {
            id
            name
            about
            demographic
            brandColors
            similarBrands
            bypassPreOrderRestriction
            domain
            julySleepExcluded
        }
        customerStage {
            designer
            launched
            connectedToDesigner
            approvedDesign
            approveManufacturingDrawing
            approveSamplePhotos
            approvePhysicalSample
            domain
        }
        modifier
        assignee
        sku
        mainSku
        designer
        dqReason
        dqDate
        dqStage
        source
        lead
        agency
        notes
        launchDate
        launchEnd
        warehousePricingVersion
        lifecycleStatus
        preDesignReviewState
        designConceptState
        mfgDrawingState
        samplingState
        designInvoiceState
        packagingState
        onlineListingState
        inventoryStatus
        asanaTaskId
        shopifyProductId
        twilioConversationId
        shippingInfo
    }
`;

export const tundraStorefrontAllFieldsFragment = gql`
    fragment TundraStorefrontAllFields on TundraStorefront {
        storefront {
            createdBy
            devThemeS3ObjectVersion
            devThemeVersion
            developmentBucket
            displayName
            prodThemeS3ObjectVersion
            prodThemeVersion
            productionBucket
            region
            shopifyDomain
            shortName
            storeId
            themeBuilderDisabled
            themeConfigVersion
            tier
        }
        domains {
            alias {
                alias
                certificateKeyAuthorization
                certificateDeploymentId
                certificateDeploymentTime
                customDomainCertificateId
                customDomainWorkflowId
                lastCertificateExpirationTime
                lastCertificateRenewal
                lastCertificateRenewalTime
                lastScannedStatus
                lastScannedTime
                storeId
            }
            certificate {
                domainNames
                primaryDomainName
                validationDnsRecords {
                    name
                    type
                    value
                }
                validationMethod
                validationStatus
            }
            certificateDeployment {
                status
                deploymentId
                startedTime
            }
            dns {
                type
                values
            }
            renewal {
                cause
                error
                input
                name
                output
                startDate
                status
                workflowId
            }
        }
    }
`;

export const payoutRuleAllFieldsFragment = gql`
    fragment PayoutRuleAllFields on PayoutRule {
        id
        name
        type
        retailPricePercentApplicable
        unitCostPercentApplicable
        juniperServiceFeePercentApplicable
        fulfillmentFeePercentApplicable
        processingFeePercentApplicable
        shippingSubsidyPercentApplicable
        vendorProfitSplitPercent
        createdAt
        updatedAt
    }
`;


export const shopifyProductVariantAllFieldsFragment = gql`
    fragment ShopifyProductVariantAllFields on ShopifyProductVariant {
        id
        shopifyProductId
        sku
        title
        price
        compareAtPrice
        inventoryQuantity
    }
`;

export const shopifyProductAllFieldsFragment = gql`
    fragment ShopifyProductAllFields on ShopifyProduct {
        id
        title
        description
        status
        vendor
        tags
        productType
        createdAt
        updatedAt
        productListing {
            id
            name
            storeId
            status
            mainSku
        }
        store {
            id
            name
        }
        hasPayoutRuleAssignments
        hasCostMetadata
        variants {
            id
            sku
            title
        }
    }
`;

export const costMetadataAllFieldsFragment = gql`
    fragment CostMetadataAllFields on CostMetadata {
        id
        shopifyAccountId
        startDate
        unitCost
        juniperServiceFee
        fulfillmentFee
        processingFeePercent
        shippingSubsidy
        modifier
        costPayoutModifierId
        costPayoutModifier {
            id
            name
            conditionCountryCode
            conditionEmailDomains
            conditionIsBundle
            conditionSourceName
            conditionFulfillmentService
            priority
        }
        shopifyProductVariant {
            id
            sku
            title
        }
        createdAt
        updatedAt
    }
`;

export const costPayoutModifierAllFieldsFragment = gql`
    fragment CostPayoutModifierAllFields on CostPayoutModifier {
        id
        name
        conditionCountryCode
        conditionEmailDomains
        conditionIsBundle
        conditionSourceName
        conditionFulfillmentService
        priority
        createdAt
        updatedAt
    }
`;

export const plprAllFieldsFragment = gql`
    fragment PLPRAllFields on ProductListingToPayoutRule {
        id
        startDate
        startThreshold
        productListingId
        payoutRuleId
        costPayoutModifierId
        payoutRule {
            id
            name
            type
            vendorProfitSplitPercent
            retailPricePercentApplicable
            unitCostPercentApplicable
            juniperServiceFeePercentApplicable
            fulfillmentFeePercentApplicable
            processingFeePercentApplicable
            shippingSubsidyPercentApplicable
        }
        costPayoutModifier {
            id
            name
        }
        createdAt
        updatedAt
    }
`;

export const importThemesFragment = gql`
    fragment ImportThemesAllFields on TundraStorefrontMoveCopyTheme {
        code
        message
        results {
            error
            success
            srcVersion
            destVersion
        }
    }
`;
